<template>
  <Loader v-if="showLoader" />
</template>

<script>
import brand from "../../helpers/brand";
import * as helpers from "../../helpers";
import smartStorage from "../../helpers/smart-storage";
import Loader from "../Utility/Loader";
import { AUTH_STATE, CUSTOM_ERROR_CODES, LOGIN_TYPE, LOGIN_ERROR_CODES } from "@/helpers/const";
import { measurePagePerformance } from "@/analytics/performance/pageTimings";
import { getLastQueryParamValue } from "@/helpers/queryParameterHelper";
import { sentryCaptureException, getJWTPayloadToLogInSentry } from "@/helpers/sentry";
import authService from "@/services/auth/auth.service";
import Logger from "@/helpers/logger";

/**
 * component responsible for auto login with partner generated JWT token
 * required query params
 * - jwt: JWT token signed with partner shared key
 * - partnerId: alpha numeric partner ID
 * - installId: plugin installation ID
 * other query params
 * - email (launchEmail): used in `launchEmailCreation` action
 * - customerEmail (loginEmail): an email for which login is done
 *  (customerEmail incase of customer login or else mailbox email)
 *  check FIXME:
 * - section: after login where to redirect
 * - action: after login what action to take (check App.vue for action and section)
 * - pluginThemeID, cpThemeId: used in widget mode to set the CP theme
 * - mode: dark | light
 * - locale: lang
 */
export default {
  name: "LoginWithJWT",
  data() {
    return {
      isTitanMail: null,
      brandDetails: {},
      showLoader: false,
      queryParams: {},
    };
  },
  components: { Loader },
  props: {
    setOfacParams: Function,
    setForceResetPasswordParams: Function,
    authState: String,
  },
  mounted() {
    measurePagePerformance();
  },
  created() {
    const isMobileBlockerScreenVisible = !helpers.shouldShowResponsiveCP();
    if (this.authState === AUTH_STATE.AUTH || isMobileBlockerScreenVisible) return;

    let partnerId = this.$route.query.partnerId;
    this.queryParams = this.$route.query;
    if (this.$route.meta.initialiseTheme) {
      // checking theme before using loader
      this.getPartnerInfo({
        headers: { "X-Auth": "noAuth" },
        params: {
          partnerId,
        },
      }).then(data => {
        const PLUGIN_THEME_ID = this.$route.query.pluginThemeID;
        const CP_THEME_ID = data?.resources?.cpThemeId;
        const MODE = this.$route.query.theme;
        this.$store.dispatch("theme", CP_THEME_ID);
        document.documentElement.setAttribute(
          "theme",
          this.getThemeInfo(PLUGIN_THEME_ID, CP_THEME_ID, MODE)
        );
        helpers.setViewAndTheme();
        this.initAutoLogin();
      });
    } else {
      this.initAutoLogin();
    }
  },
  methods: {
    getThemeInfo(PLUGIN_THEME_ID, CP_THEME_ID, MODE) {
      const DEFAULT_THEME = "defaultTheme";
      return (
        PLUGIN_THEME_ID ||
        (CP_THEME_ID ? (MODE ? `${CP_THEME_ID}_${MODE}` : CP_THEME_ID) : DEFAULT_THEME)
      );
    },
    showLoginErrorScreen(err) {
      console.error("auto login failed with error: ", err);
      this.showLoader = false;
      sentryCaptureException(
        err,
        { loginFailure: "jwtToken" },
        {
          jwtPayload: JSON.stringify(getJWTPayloadToLogInSentry(this.queryParams?.jwt)),
        }
      );
      this.$router.replace({ name: "LoginFailureScreen" });
    },
    initAutoLogin() {
      this.showLoader = true;
      this.setBrandDetails();
      const partnerId = this.$route.query.partnerId;
      const jwt = this.$route.query.jwt;
      const installId = this.$route.query.installId;
      if (this.$route.query.section)
        smartStorage.setItem("section", getLastQueryParamValue(this.$route.query, "section"));
      // used in `launchEmailCreation` action
      if (this.$route.query.email)
        smartStorage.setItem("launchEmail", getLastQueryParamValue(this.$route.query, "email"));
      // FIXME: this neo check can be removed
      // use latest /autoLogin route instead
      if (this.$route.query.customerEmail && this.$route.query.isFromNeo)
        smartStorage.setItem(
          "loginEmail",
          getLastQueryParamValue(this.$route.query, "customerEmail")
        );
      if (this.$route.query.action)
        smartStorage.setItem("action", getLastQueryParamValue(this.$route.query, "action"));

      this.clearQueryParams();

      try {
        this.handleJWTLogin(jwt, partnerId, installId);
      } catch (err) {
        this.showLoginErrorScreen(err);
        return;
      }
    },
    clearQueryParams() {
      let query = Object.assign({}, this.$route.query);
      delete query.jwt;
      delete query.partnerId;
      delete query.installId;
      this.$router.replace({ query });
    },
    setBrandDetails() {
      this.isTitanMail =
        this.isTitanMail === null
          ? brand.deriveBrandFromURL() === brand.NAMES.TITAN
          : this.isTitanMail;
      this.brandDetails = brand.getDetails();
    },
    handleJWTLogin(jwt, partnerId, installId) {
      const data = { jwt };
      if (installId) {
        data.serverId = installId;
      } else {
        data.partnerId = partnerId;
      }

      if (!jwt) {
        return this.onError(helpers.getBllFormattedError(CUSTOM_ERROR_CODES.MISSING_JWT));
      }

      return authService
        .loginWithJWT(jwt, partnerId, installId)
        .then(this.onSuccess)
        .catch(this.onError);
    },
    onSuccess(data) {
      if (data) {
        this.$emit("onLoginSuccess", LOGIN_TYPE.LOGIN_WITH_JWT);
      }
    },
    onError(err) {
      Logger.error("`LoginWithJWT` error", err);
      if (err?.data?.code === "OFAC_SANCTIONED_COUNTRY") {
        this.setOfacParams({
          isOfacFlag: true,
          ofacDocumentation: this.brandDetails.ofacDocumentation,
          supportEmail: this.brandDetails.supportEmail,
          brandLogo: this.brandDetails.logo,
        });
        this.medusaEvents.sendPreAuthEvent(
          this.medusaEvents.createEventObj(
            {
              eventName: "ofac_login_denied",
              ofac_country: err.data.attrs.countryName,
              // is_first_login
            },
            helpers.getDomainName(),
            "flock_order"
          )
        );
      } else if (err?.data?.code === LOGIN_ERROR_CODES.FORCE_RESET_PASSWORD) {
        this.setForceResetPasswordParams({
          isForceResetPasswordFlag: true,
          brandLogo: this.brandDetails.logo,
          brandName: this.brandDetails.name,
        });
      } else {
        this.showLoginErrorScreen(err);
      }
    },
  },
};
</script>
